import type { ReadinessColumn, ReadinessTone } from "@/components/visual/ink-readiness";
import { diffDays } from "@/engine/date";
import type { Project } from "@/engine/types";
import { inspectPlan, type PlanTone } from "@/features/plans/plan-gallery";

/**
 * 포트폴리오의 경과별 준비.
 * 높이는 공정이고, 가로축은 착수 후 지난 날이다.
 * 기준은 착수에서 마감까지 고르게 올라가는 공정이다.
 */

/** 기준보다 이만큼(%p) 낮으면 주의. */
export const READINESS_WATCH_GAP = -10;
/** 기준보다 이만큼(%p) 낮으면 위험. */
export const READINESS_RISK_GAP = -25;

export interface ReadinessSource {
  id: string;
  name: string;
  start: string;
  finish: string;
  /** 공정. 0–100 */
  pct: number;
  schedule: PlanTone;
  lifecycle?: "active" | "done" | "archived" | "trashed";
}

export interface PortfolioReadiness {
  columns: ReadinessColumn[];
  /** 기준에 아직 닿지 않은 수. */
  count: number;
  /** 올해 기준에 닿은 수. 없으면 생략한다. */
  standardLabel?: string;
  /** 기준에 닿은 플랜의 착수–마감 평균. 없으면 생략한다. */
  averageLabel?: string;
  /** 기준에서 가장 멀리 있는 플랜. */
  focusKey?: string;
}

interface Scored {
  key: string;
  name: string;
  elapsedDays: number;
  elapsed: string;
  value: number;
  tone: ReadinessTone;
  verdict: string;
  gap: number;
  finish: string;
  start: string;
  callout?: string;
}

const VERDICT: Record<ReadinessTone, string> = {
  neutral: "진행",
  good: "정상",
  watch: "주의",
  risk: "위험",
};

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function buildPortfolioReadiness(sources: readonly ReadinessSource[], today: string): PortfolioReadiness {
  const year = today.slice(0, 4);
  const scored: Scored[] = [];
  for (const source of sources) {
    if ((source.lifecycle ?? "active") !== "active") continue;
    const pct = clampPct(source.pct);
    const elapsed = diffDays(source.start, today);
    const span = Math.max(1, diffDays(source.start, source.finish));
    const along = Math.max(0, Math.min(span, elapsed));
    const expected = Math.round((along / span) * 100);
    const gap = pct - expected;
    const reached = source.schedule === "done" || pct >= 100;
    const tone: ReadinessTone = reached ? "good" : gap <= READINESS_RISK_GAP ? "risk" : gap <= READINESS_WATCH_GAP || source.schedule === "late" ? "watch" : "neutral";
    scored.push({
      key: source.id,
      name: source.name,
      elapsedDays: elapsed,
      elapsed: elapsed <= 0 ? "착수 전" : `${elapsed}일`,
      value: pct,
      tone,
      verdict: VERDICT[tone],
      gap,
      finish: source.finish,
      start: source.start,
    });
  }
  scored.sort((a, b) => a.elapsedDays - b.elapsedDays || a.name.localeCompare(b.name, "ko"));

  const behind = scored.filter((row) => row.tone === "watch" || row.tone === "risk");
  behind.sort((a, b) => a.gap - b.gap || b.elapsedDays - a.elapsedDays);
  const worst = behind[0];
  if (worst) {
    worst.callout = worst.gap < 0 ? `기준보다 ${Math.abs(worst.gap)}%p 낮음` : "마감이 지났습니다";
  }

  const good = scored.filter((row) => row.tone === "good");
  const onStandardThisYear = good.filter((row) => row.finish.startsWith(year)).length;
  const days = good.map((row) => diffDays(row.start, row.finish)).filter((day) => day >= 0);
  const average = days.length === 0 ? null : Math.round(days.reduce((sum, day) => sum + day, 0) / days.length);

  return {
    columns: scored.map(({ elapsedDays: _elapsedDays, gap: _gap, finish: _finish, start: _start, ...column }) => column),
    count: scored.filter((row) => row.tone !== "good").length,
    standardLabel: onStandardThisYear > 0 ? `올해 ${onStandardThisYear}개 기준 안` : undefined,
    averageLabel: average == null ? undefined : `기준까지 평균 ${average}일`,
    focusKey: worst?.key,
  };
}

export function portfolioReadiness(projects: readonly Project[], today: string): PortfolioReadiness {
  return buildPortfolioReadiness(
    projects.map((project) => {
      const snap = inspectPlan(project, today);
      return {
        id: project.id,
        name: project.name,
        start: project.start,
        finish: snap.finish,
        pct: snap.pct,
        schedule: snap.tone,
        lifecycle: snap.lifecycle,
      };
    }),
    today,
  );
}
